import React, { useState } from 'react';
import { FaTimes, FaTrash, FaExclamationTriangle, FaInfoCircle, FaSpinner } from 'react-icons/fa';

const DeleteJobModal = ({ isOpen, onClose, job, onDelete }) => {
  const [deleting, setDeleting] = useState(false);
  const [confirmText, setConfirmText] = useState('');

  if (!isOpen || !job) return null;

  const canDelete = () => {
    // Jobs that are in progress or completed cannot be deleted
    return !['IN_PROGRESS', 'COMPLETED', 'DELIVERED'].includes(job.progress) && 
           !['IN_PROGRESS', 'COMPLETED'].includes(job.status);
  };

  const getDeleteWarning = () => {
    if (job.progress === 'PICKED_UP') {
      return 'This job has been picked up and is in progress. Consider cancelling instead of deleting.';
    }
    if (job.driverId) {
      return 'This job is assigned to a driver. Deleting it will affect driver records.';
    }
    if (job.photos && job.photos.length > 0) {
      return 'This job has associated photos that will also be deleted.';
    }
    return 'This action cannot be undone. All job data will be permanently removed.';
  };

  const handleDelete = async () => {
    if (confirmText !== 'DELETE') {
      return;
    }

    setDeleting(true);
    try {
      await onDelete(job.id);
      onClose();
    } catch (error) {
      console.error('Error deleting job:', error);
    } finally {
      setDeleting(false);
    }
  };

  const isDeletable = canDelete();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b bg-red-600 text-white rounded-t-lg">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-white bg-opacity-20 rounded-lg">
              <FaTrash className="h-5 w-5" />
            </div>
            <div>
              <h2 className="text-xl font-bold">Delete Job</h2>
              <p className="text-red-100 text-sm">Permanently remove this job</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:text-red-200 transition-colors"
          >
            <FaTimes className="h-5 w-5" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {/* Job Info */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="font-semibold text-gray-800 mb-2">Job Details</h3>
            <div className="text-sm text-gray-600 space-y-1">
              <p><strong>Route:</strong> {job.route || `${job.pickupLocation} → ${job.deliveryLocation}`}</p>
              <p><strong>Status:</strong> {job.status}</p>
              <p><strong>Progress:</strong> {job.progress}</p>
              <p><strong>Price:</strong> {job.priceFormatted || `$${job.price || 0}`}</p>
              {job.driverId && (
                <p><strong>Driver:</strong> Assigned</p>
              )}
            </div>
          </div>

          {/* Warning Messages */}
          {!isDeletable && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-start space-x-2">
                <FaExclamationTriangle className="text-red-500 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-red-700">
                  <p className="font-medium">This job cannot be deleted</p>
                  <p className="mt-1">Jobs that are in progress or completed cannot be deleted. Consider cancelling the job instead.</p>
                </div>
              </div>
            </div>
          )}

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-start space-x-2">
              <FaInfoCircle className="text-yellow-500 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-yellow-700">
                <p className="font-medium">Warning</p>
                <p className="mt-1">{getDeleteWarning()}</p>
              </div>
            </div>
          </div>

          {/* Confirmation Input */}
          {isDeletable && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Type "DELETE" to confirm
              </label>
              <input
                type="text"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="DELETE"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-red-500 focus:border-red-500"
                disabled={deleting}
              />
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end space-x-3 p-6 border-t bg-gray-50 rounded-b-lg">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors"
            disabled={deleting}
          >
            Cancel
          </button>
          {isDeletable ? (
            <button
              onClick={handleDelete}
              disabled={confirmText !== 'DELETE' || deleting}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
            >
              {deleting ? (
                <>
                  <FaSpinner className="animate-spin mr-2" />
                  Deleting...
                </>
              ) : (
                <>
                  <FaTrash className="mr-2" />
                  Delete Job
                </>
              )}
            </button>
          ) : (
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
            >
              Close
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default DeleteJobModal;
